/**
 * SVC-TYPE — the service-type catalog + fee resolution for the booking modal.
 * Types are admin-managed (Settings → Service Types); fees resolve from the
 * editable fee schedule (the live version of Diagnostic Fees 12-02-2024) via the
 * resolve_fee RPC, which picks the right number from customer context (PLUS plan,
 * zone, after-hours, segment) — so the dispatcher sees the correct fee, not a guess.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson } from "@/lib/orgSettings";

export type Urgency = "asap" | "soon" | "routine";

export interface ServiceType {
  id: string;
  name: string;
  category: string;
  default_description: string | null;
  default_urgency: Urgency;
  est_minutes: number;
  skills: string[];
  fee_code: string | null;
  routes_to_empno: string | null;
  active: boolean;
  sort_order: number;
  /** Per-type checklist template (one item per entry) — the truck shows it on
   *  the call and ticked items print on the invoice. Null/empty = none. */
  checklist: string[] | null;
  /** SKILL-STEER-01 (Settings → Skills & Levels): the lowest tech level for
   *  this call (1–3, names in org_setting tech_levels); null = anyone. */
  min_level: number | null;
  /** Specialties (skill_domain ids) the call needs — Boiler → Boiler Service. */
  needs_skills: number[];
  /** Techs the booking grid offers first when they fit. */
  offer_first: string[];
}

const CATEGORY_LABEL: Record<string, string> = {
  maintenance: "Maintenance / tune-ups",
  service: "Service calls",
  sales: "Estimates",
  install: "Installs",
  followup: "Follow-up",
  plumbing: "Plumbing",
};

export function categoryLabel(c: string): string {
  return CATEGORY_LABEL[c] ?? c;
}

export function useServiceTypes() {
  return useQuery({
    queryKey: ["service_types"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ServiceType[]> => {
      const { data, error } = await supabase
        .from("service_type")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ServiceType[];
    },
  });
}

/**
 * Downtown ZIPs get the higher diagnostic rate (parking/access). This default
 * (core downtown Mpls + St Paul) is overridden by the admin-editable
 * `downtown_zips` org setting (Settings → Rules).
 */
export const DEFAULT_DOWNTOWN_ZIPS = [
  "55401", "55402", "55403", "55404", "55405", "55415", // downtown Mpls
  "55101", "55102", // downtown St Paul
];

export interface BusinessHours {
  start: number;
  end: number;
}
export const DEFAULT_BUSINESS_HOURS: BusinessHours = { start: 7, end: 17 };

/** Admin-editable business hours (Settings → Rules); defines "after-hours". */
export function useBusinessHours() {
  return useOrgSettingJson<BusinessHours>("business_hours", DEFAULT_BUSINESS_HOURS);
}

/** Is "now" (Central) outside the shop's business hours? Weekends are after-hours. */
export function isAfterHoursFor(bh: BusinessHours): boolean {
  const ct = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }),
  );
  const day = ct.getDay();
  const h = ct.getHours();
  if (day === 0 || day === 6) return true;
  return h < bh.start || h >= bh.end;
}

export interface FeeContext {
  downtown: boolean;
  segment: "residential" | "commercial";
  zone: string;
}

/** The customer's fee context — zone (→ downtown, from the admin ZIP list) +
 * segment. Segment defaults to residential; commercial detection is a follow-on. */
export function useFeeContext(
  custno: string | null | undefined,
  locno: string | null | undefined,
) {
  const { data: dtZips } = useOrgSettingJson<string[]>(
    "downtown_zips",
    DEFAULT_DOWNTOWN_ZIPS,
  );
  const zips = dtZips ?? DEFAULT_DOWNTOWN_ZIPS;
  return useQuery({
    queryKey: ["fee_context", custno, locno, zips.join(",")],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FeeContext> => {
      const { data } = await supabase
        .from("mirror_location")
        .select("zone")
        .eq("custno", custno as string)
        .eq("locno", locno ?? "00001")
        .limit(1)
        .maybeSingle();
      const zone = String((data as { zone: string | null } | null)?.zone ?? "").trim();
      return { downtown: zips.includes(zone), segment: "residential", zone };
    },
  });
}

export interface ResolvedFee {
  amount: number;
  label: string;
  unit: string;
}

export function useResolveFee(
  feeCode: string | null | undefined,
  ctx: { plus?: boolean; segment?: string; downtown?: boolean; afterHours?: boolean },
) {
  const {
    plus = false,
    segment = "residential",
    downtown = false,
    afterHours = false,
  } = ctx;
  return useQuery({
    queryKey: ["resolve_fee", feeCode, plus, segment, downtown, afterHours],
    enabled: !!feeCode,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ResolvedFee | null> => {
      const { data, error } = await supabase.rpc("resolve_fee", {
        p_fee_code: feeCode,
        p_segment: segment,
        p_plus: plus,
        p_downtown: downtown,
        p_after_hours: afterHours,
      });
      if (error) return null;
      const rows = (data ?? []) as ResolvedFee[];
      return rows[0] ?? null;
    },
  });
}

// ─────────────────────────── admin CRUD (Settings) ──────────────────────────

export const FEE_CODES = [
  "frn_ac_diag",
  "boiler_diag",
  "rooftop_diag",
  "minisplit_diag",
  "demand_tuneup",
  "demand_tuneup_boiler",
  "demand_tuneup_minisplit",
  "humidifier",
  "air_exchanger",
  "leak_search",
  "furnace_cert",
  "chimney_survey",
  "chimney_cap",
  "tm",
  "nest_install",
  "refrigerant_r410",
  "refrigerant_r22",
];

export const CATEGORIES = [
  "maintenance",
  "service",
  "sales",
  "install",
  "followup",
  "plumbing",
];

/** All types incl. inactive — the admin list. */
export function useAllServiceTypes() {
  return useQuery({
    queryKey: ["service_types_all"],
    queryFn: async (): Promise<ServiceType[]> => {
      const { data, error } = await supabase
        .from("service_type")
        .select("*")
        .order("category")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ServiceType[];
    },
  });
}

export function useSaveServiceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<ServiceType> & { id: string }) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("service_type")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_types"] });
      qc.invalidateQueries({ queryKey: ["service_types_all"] });
    },
  });
}

export function useAddServiceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("service_type").insert({
        name: "New service type",
        category: "service",
        default_urgency: "routine",
        est_minutes: 60,
        sort_order: 999,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_types"] });
      qc.invalidateQueries({ queryKey: ["service_types_all"] });
    },
  });
}

export interface FeeItem {
  id: string;
  fee_code: string;
  label: string;
  section: string;
  amount: number;
  unit: string;
  segment: string;
  plan_scope: string;
  zone: string;
  after_hours: boolean;
  note: string | null;
  active: boolean;
  sort_order: number;
}

export function useFeeItems() {
  return useQuery({
    queryKey: ["fee_items"],
    queryFn: async (): Promise<FeeItem[]> => {
      const { data, error } = await supabase
        .from("fee_item")
        .select("*")
        .order("section")
        .order("fee_code")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as FeeItem[];
    },
  });
}

export function useSaveFeeItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<FeeItem> & { id: string }) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("fee_item")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fee_items"] });
      qc.invalidateQueries({ queryKey: ["resolve_fee"] });
    },
  });
}

export interface PlanDiscount {
  id: string;
  min_years: number;
  repair_pct: number;
  label: string;
  active: boolean;
  sort_order: number;
}

export function usePlanDiscounts() {
  return useQuery({
    queryKey: ["plan_discounts"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PlanDiscount[]> => {
      const { data, error } = await supabase
        .from("plan_discount")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PlanDiscount[];
    },
  });
}

export function useSavePlanDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<PlanDiscount> & { id: string }) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("plan_discount")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan_discounts"] }),
  });
}

export function useAddPlanDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("plan_discount")
        .insert({ min_years: 1, repair_pct: 10, label: "New Tier", sort_order: 999 });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan_discounts"] }),
  });
}

export function useDeletePlanDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("plan_discount").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan_discounts"] }),
  });
}

export interface NewFeeItem {
  fee_code: string;
  label: string;
  section: string;
  amount: number;
  unit?: string;
  segment?: string;
  plan_scope?: string;
  zone?: string;
  after_hours?: boolean;
}

export function useAddFeeItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NewFeeItem) => {
      const { error } = await supabase.from("fee_item").insert({
        fee_code: v.fee_code,
        label: v.label,
        section: v.section,
        amount: v.amount,
        unit: v.unit ?? "flat",
        segment: v.segment ?? "residential",
        plan_scope: v.plan_scope ?? "standard",
        zone: v.zone ?? "any",
        after_hours: v.after_hours ?? false,
        sort_order: 999,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fee_items"] });
      qc.invalidateQueries({ queryKey: ["resolve_fee"] });
    },
  });
}
